import React, { useState, useEffect } from 'react';
import { namecheapAPI } from '../api';

function DomainList() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await namecheapAPI.getDomains();
      // Handle both { domains: [...] } and direct array responses
      const domainsData = response.data?.domains || response.data || [];
      setDomains(Array.isArray(domainsData) ? domainsData : []);
      setError('');
    } catch (err) {
      console.error('Error fetching domains:', err);
      setError(err.response?.data?.error || 'Failed to fetch domains');
      setDomains([]); // Ensure domains is always an array
    } finally {
      setLoading(false);
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  };

  const calculateDaysUntilExpiry = (expDate) => {
    const expiry = parseDate(expDate);
    if (!expiry) return null;
    const today = new Date();
    const daysLeft = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    return daysLeft;
  };

  const formatDate = (dateStr) => {
    const date = parseDate(dateStr);
    return date ? date.toLocaleDateString() : 'N/A';
  };

  const getStatusBadge = (domain) => {
    if (domain.isExpired) {
      return <span className="badge badge-danger">EXPIRED</span>;
    }
    const daysLeft = calculateDaysUntilExpiry(domain.expiration);
    if (daysLeft === null) {
      return <span className="badge badge-success">ACTIVE</span>;
    }
    if (daysLeft < 45) {
      return <span className="badge badge-warning">EXPIRING SOON ({daysLeft}d)</span>;
    }
    return <span className="badge badge-success">ACTIVE</span>;
  };

  if (loading) {
    return <div className="loading">Loading domains...</div>;
  }

  return (
    <div className="component-container">
      <div className="component-header">
        <h2>Domain Management</h2>
        <button className="btn-primary" onClick={fetchDomains}>
          Refresh
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Status</th>
              <th>Expiration Date</th>
              <th>Days Left</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {domains.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">
                  No domains found
                </td>
              </tr>
            ) : (
              domains
                .sort((a, b) => {
                  const daysA = calculateDaysUntilExpiry(a.expiration) ?? Infinity;
                  const daysB = calculateDaysUntilExpiry(b.expiration) ?? Infinity;
                  return daysA - daysB; // Sort by days until expiry
                })
                .map((domain) => {
                  const daysLeft = calculateDaysUntilExpiry(domain.expiration);
                  return (
                    <tr key={domain.name}>
                      <td className="domain-name">
                        <strong>{domain.name}</strong>
                      </td>
                      <td>{getStatusBadge(domain)}</td>
                      <td>{formatDate(domain.expiration)}</td>
                      <td>
                        {daysLeft !== null ? `${daysLeft} days` : 'N/A'}
                      </td>
                      <td>{formatDate(domain.created)}</td>
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
      </div>

      <div className="component-footer">
        <p className="text-small">
          💡 Tip: Domains expiring within 45 days are highlighted. Consider renewing early.
        </p>
      </div>
    </div>
  );
}

export default DomainList;
